import { jobApplicationStatusLabels, jobApplicationSubStatusLabels } from '../../statuses';
import { JobApplicationRaw } from '../../types';

export type ExportColumnGroup = 'talent' | 'application' | 'job';

export interface ExportColumn {
    id: string;
    label: string;
    group: ExportColumnGroup;
    getValue: (app: JobApplicationRaw) => string;
}

export const EXPORT_COLUMN_GROUPS: Record<ExportColumnGroup, string> = {
    talent: 'Informations du talent',
    application: 'Candidature',
    job: "Offre d'emploi",
};

const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

type ExtendedJobApplication = JobApplicationRaw & {
    reference?: string;
    jobOffer: JobApplicationRaw['jobOffer'] & { jobNumber?: string };
};

export const ALL_EXPORT_COLUMNS: ExportColumn[] = [
    // Talent
    {
        id: 'talentFullName',
        label: 'Nom complet',
        group: 'talent',
        getValue: (app) => [app.talent?.firstName, app.talent?.lastName].filter(Boolean).join(' '),
    },
    {
        id: 'talentEmail',
        label: 'Email',
        group: 'talent',
        getValue: (app) => app.talent?.email ?? '',
    },
    {
        id: 'talentCity',
        label: 'Ville',
        group: 'talent',
        getValue: (app) => app.talent?.city ?? '',
    },
    {
        id: 'talentProfession',
        label: 'Profession',
        group: 'talent',
        getValue: (app) => app.talent?.principalProfession?.professionName ?? '',
    },
    {
        id: 'talentYearsOfExperience',
        label: "Années d'expérience",
        group: 'talent',
        getValue: (app) => app.talent?.yearsOfExperience != null ? String(app.talent.yearsOfExperience) : '',
    },
    // Application
    {
        id: 'status',
        label: 'Statut',
        group: 'application',
        getValue: (app) =>
            jobApplicationStatusLabels[app.status as keyof typeof jobApplicationStatusLabels] ?? app.status ?? '',
    },
    {
        id: 'subStatus',
        label: 'Sous-statut',
        group: 'application',
        getValue: (app) =>
            app.subStatus
                ? (jobApplicationSubStatusLabels[app.subStatus as keyof typeof jobApplicationSubStatusLabels] ?? app.subStatus)
                : '',
    },
    {
        id: 'applicationDate',
        label: 'Date de candidature',
        group: 'application',
        getValue: (app) => formatDate(app.date),
    },
    {
        id: 'reference',
        label: 'Référence',
        group: 'application',
        getValue: (app) => (app as ExtendedJobApplication).reference ?? '',
    },
    {
        id: 'rating',
        label: 'Note Upercut',
        group: 'application',
        getValue: (app) => app.upercutAverageRating != null ? String(app.upercutAverageRating) : '',
    },
    {
        id: 'candidateRating',
        label: 'Évaluation candidat',
        group: 'application',
        getValue: (app) => app.rating != null ? String(app.rating) : '',
    },
    {
        id: 'aiScore',
        label: 'Score IA',
        group: 'application',
        getValue: (app) => app.resumeScore != null ? String(app.resumeScore) : '',
    },
    // Job
    {
        id: 'jobTitle',
        label: "Titre de l'offre",
        group: 'job',
        getValue: (app) => app.jobOffer?.title ?? '',
    },
    {
        id: 'jobNumber',
        label: "Numéro d'offre",
        group: 'job',
        getValue: (app) => (app as ExtendedJobApplication).jobOffer?.jobNumber ?? '',
    },
    {
        id: 'contractType',
        label: 'Type de contrat',
        group: 'job',
        getValue: (app) => app.jobOffer?.contract?.status ?? '',
    },
];

export const buildCsvRows = (
    apps: JobApplicationRaw[],
    selectedColumnIds: string[],
): Record<string, string>[] => {
    const selectedCols = ALL_EXPORT_COLUMNS.filter((col) => selectedColumnIds.includes(col.id));
    return apps.map((app) => {
        const row: Record<string, string> = {};
        selectedCols.forEach((col) => {
            row[col.label] = col.getValue(app);
        });
        return row;
    });
};

export const buildFilename = (jobTitle?: string, jobNumber?: string): string => {
    const sanitize = (str: string) => str.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
    const titlePart = jobTitle ? sanitize(jobTitle) : 'candidatures';
    const numberPart = jobNumber ? sanitize(jobNumber) : '';
    const now = new Date();
    const datePart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const timePart = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    return [titlePart, numberPart, datePart, timePart].filter(Boolean).join('_') + '.csv';
};

/**
 * Triggers a native browser CSV download with a UTF-8 BOM for correct Excel rendering.
 */
export const downloadCsvFile = (rows: Record<string, string>[], filename: string): void => {
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const escape = (val: string) => `"${(val ?? '').replace(/"/g, '""')}"`;
    const lines = [
        headers.map(escape).join(','),
        ...rows.map((row) => headers.map((h) => escape(row[h] ?? '')).join(',')),
    ];
    const csvContent = '\uFEFF' + lines.join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
